import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { toaster } from '@/components/ui/toaster.tsx';
import { createCustomEntryV2HousesHouseIdMaintenanceCustomEntriesPostMutation } from '@/client/@tanstack/react-query.gen';
import { usePlanRefresh } from '@/hooks/maintenance/usePlanRefresh';
import { isSubmittableCadence, toCustomEntryPayload } from '@/components/maintenance/model/customEntry.ts';
import { makeLogger } from '@/utils/logger/logger.ts';
import { formatLocalDateForApi } from '@/utils/dateUtils.ts';
import type { CustomEntryOut } from '@/client';
import {
	DEFAULT_RECURRING_SELECTION,
	FrequencyValue,
	mapFrequencySelectionToString,
} from '@/utils/maintenance/frequencyUtils.ts';

const logger = makeLogger('useTaskComposer');

/**
 * The existing entry a draft is being hung beneath, as the form reads it — a title to name it by and
 * an id to hang it on. Deliberately not a plan node: sub-tasks are still a composer-side idea, and
 * widening this to `PlanNodeOut` would claim a relationship the API does not yet express.
 */
export interface TaskComposerParent {
	id: number;
	title: string;
}

export interface TaskScheduleController {
	startDate: string;
	setStartDate: (value: string) => void;
	frequency: string;
	setFrequency: (value: string) => void;
	isRecurring: boolean;
	setIsRecurring: (value: boolean) => void;
	toggleRecurring: () => void;
	isSubmitting?: boolean;
}

export interface TaskComposerChildDraft extends TaskScheduleController {
	id: string;
	title: string;
	description: string;
}

export interface TaskComposerController {
	title: string;
	setTitle: (value: string) => void;
	description: string;
	setDescription: (value: string) => void;
	isDirty: boolean;
	/** ISO yyyy-mm-dd. */
	startDate: string;
	setStartDate: (value: string) => void;
	frequency: string;
	setFrequency: (value: string) => void;
	isRecurring: boolean;
	setIsRecurring: (value: boolean) => void;
	/** Flip recurrence to its opposite — the one-tap affordance the composer bar exposes. */
	toggleRecurring: () => void;
	canSubmit: boolean;
	isSubmitting: boolean;
	submit: () => Promise<void>;
	reset: () => void;
	children: TaskComposerChildDraft[];
	removingChildIds: string[];
	addChild: () => void;
	removeChild: (id: string) => void;
	completeChildRemoval: (id: string) => void;
	updateChild: (id: string, patch: Partial<Omit<TaskComposerChildDraft, 'id'>>) => void;
	childCreationEnabled: boolean;
	parentTask?: TaskComposerParent;
}

interface UseTaskComposerOptions {
	/** Called once the task has been created, so the host can collapse the composer. */
	onSubmitted: (entry: CustomEntryOut) => void;
	/** Enables the client-only grouped-task trial until parent/child API fields land. */
	childCreationEnabled?: boolean;
	/** Adds the composed task beneath an existing trial parent instead of creating a new top-level task. */
	parentTask?: TaskComposerParent;
}

/**
 * Owns the Task tab's form and commit — a household writing its own entry into the plan.
 *
 * The entry is stored as an INPUT to the plan, not an actionable card. A schedule host can show that
 * input as an inert pending row, but the backend still has to materialise its dates and resolve target,
 * which is why the refetch is scheduled as well as immediate. Recurrence is fixed at creation because
 * the entry's occurrence keys derive from it, so an unexpressable cadence fails `canSubmit` rather
 * than being corrected after the fact.
 */
export const useTaskComposer = (options: UseTaskComposerOptions): TaskComposerController => {
	const { onSubmitted, childCreationEnabled = false, parentTask } = options;
	const { house } = useCurrentHouse();
	const refreshPlan = usePlanRefresh(house.id);
	const { mutateAsync: createCustomEntry, isPending: isSubmitting } = useMutation(
		createCustomEntryV2HousesHouseIdMaintenanceCustomEntriesPostMutation(),
	);

	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [startDate, setStartDate] = useState(() => formatLocalDateForApi());
	const [frequency, setFrequency] = useState<string>(FrequencyValue.Once);
	const [isRecurring, setIsRecurring] = useState(false);
	const [children, setChildren] = useState<TaskComposerChildDraft[]>([]);
	const [removingChildIds, setRemovingChildIds] = useState<string[]>([]);
	// React Query's `isPending` arrives on the next render. Hold the write synchronously as well so
	// two clicks in one tick cannot create two entries before the button has had a chance to disable.
	const submissionInFlightRef = useRef(false);

	// A title and a due date are required; details are optional.
	// TODO(schedule/custom-entry-date): Consider allowing an undated user-created entry once the
	// contract and UI can distinguish as-needed maintenance from event-conditional guidance, and
	// undated work has a deliberate way to resurface rather than quietly living in the As needed tab.
	const isDirty = title.trim().length > 0 || description.trim().length > 0 || children.length > 0;
	const childrenAreValid = children.every((child) => child.title.trim().length > 0 && child.startDate.length > 0);
	const canSubmit =
		title.trim().length > 0 &&
		startDate.length > 0 &&
		isSubmittableCadence({ frequency, isRecurring }) &&
		childrenAreValid &&
		removingChildIds.length === 0 &&
		!isSubmitting;

	const reset = useCallback(() => {
		setTitle('');
		setDescription('');
		setStartDate(formatLocalDateForApi());
		setFrequency(FrequencyValue.Once);
		setIsRecurring(false);
		setChildren([]);
		setRemovingChildIds([]);
	}, []);

	const handleRecurringChange = (value: boolean) => {
		setIsRecurring(value);
		setFrequency(value ? mapFrequencySelectionToString(DEFAULT_RECURRING_SELECTION) : FrequencyValue.Once);
	};

	const updateChild = (id: string, patch: Partial<Omit<TaskComposerChildDraft, 'id'>>) => {
		setChildren((current) => current.map((child) => (child.id === id ? { ...child, ...patch } : child)));
	};

	const addChild = () => {
		const id = globalThis.crypto?.randomUUID?.() ?? `child-${Date.now()}`;
		setChildren((current) => [
			...current,
			{
				id,
				title: '',
				description: '',
				startDate: startDate || formatLocalDateForApi(),
				frequency: FrequencyValue.Once,
				isRecurring: false,
				setStartDate: (value) => updateChild(id, { startDate: value }),
				setFrequency: (value) => updateChild(id, { frequency: value }),
				setIsRecurring: (value) =>
					updateChild(id, {
						isRecurring: value,
						frequency: value
							? mapFrequencySelectionToString(DEFAULT_RECURRING_SELECTION)
							: FrequencyValue.Once,
					}),
				toggleRecurring: () => {
					setChildren((latest) =>
						latest.map((child) =>
							child.id === id
								? {
										...child,
										isRecurring: !child.isRecurring,
										frequency: !child.isRecurring
											? mapFrequencySelectionToString(DEFAULT_RECURRING_SELECTION)
											: FrequencyValue.Once,
									}
								: child,
						),
					);
				},
			},
		]);
	};

	const submit = async () => {
		if (!canSubmit || submissionInFlightRef.current) return;

		// TODO(schedule/sub-tasks): a custom entry is flat — the create endpoint expresses no parent or
		// child, so sending one of these drafts would keep the title and quietly lose the structure the
		// household built. The grouped composer stays behind `childCreationEnabled`, which is off in the
		// app and on only in Storybook, until the API can hold a relationship.
		if (parentTask || children.length > 0) {
			logger.warn('Grouped task drafts have no v2 representation yet; not submitting', {
				childCount: children.length,
				hasParent: Boolean(parentTask),
			});
			return;
		}

		submissionInFlightRef.current = true;
		let createdEntry: CustomEntryOut;
		try {
			createdEntry = await createCustomEntry({
				path: { house_id: house.id },
				body: toCustomEntryPayload({ title, description, startDate, frequency, isRecurring }),
			});
		} catch (error) {
			logger.error('Failed to create custom entry', error);
			toaster.create({ title: 'Failed to add task. Please try again.', type: 'error' });
			return;
		} finally {
			submissionInFlightRef.current = false;
		}

		reset();
		onSubmitted(createdEntry);
		refreshPlan();
	};

	return {
		title,
		setTitle,
		description,
		setDescription,
		isDirty,
		startDate,
		setStartDate,
		frequency,
		setFrequency,
		isRecurring,
		setIsRecurring: handleRecurringChange,
		toggleRecurring: () => handleRecurringChange(!isRecurring),
		canSubmit,
		isSubmitting,
		submit,
		reset,
		children,
		removingChildIds,
		addChild,
		removeChild: (id) => {
			setRemovingChildIds((current) => (current.includes(id) ? current : [...current, id]));
		},
		completeChildRemoval: (id) => {
			setChildren((current) => current.filter((child) => child.id !== id));
			setRemovingChildIds((current) => current.filter((childId) => childId !== id));
		},
		updateChild,
		childCreationEnabled: childCreationEnabled && !parentTask,
		parentTask,
	};
};
